// NSARA Stores V2 — vitrine publique d'une boutique (section 18 de la mission).
//
// Page 100% publique: aucune authentification, aucune coquille d'application
// (ni sidebar, ni tableau de bord) — seulement l'identité du marchand en
// premier, son catalogue, et le lien de commande de chaque produit.
//
// Source de vérité: GET /api/public/shops/:shopId (backend/routes/publicShops.js).
// Le backend ne renvoie QUE le catalogue public de CETTE boutique (produits
// actifs + public_enabled, filtrés shop_id = :shopId strict): cette page ne
// peut donc jamais afficher le produit d'une autre boutique.
//
// Identifiant: l'id (uuid) de la boutique — comme un produit réutilise déjà son
// public_id pour /p/:publicId. Aucun slug, aucune migration.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ImageOff, Search, Store as StoreIcon } from 'lucide-react';
import { getPublicShop } from '../services/apiClient';
import { accentForShop, buildPublicProductUrl, formatMoney, shopMonogram } from './boutiques/storeUtils';
import '../styles/stores.css';

export default function PublicStorefront() {
  const { shopId } = useParams();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [state, setState] = useState({ loading: true, error: '' });

  useEffect(() => {
    let live = true;
    setState({ loading: true, error: '' });
    setShop(null);
    setProducts([]);
    setQuery('');
    setCategory('');
    getPublicShop(shopId)
      .then((data) => {
        if (!live) return;
        setShop(data.shop);
        setProducts(data.products || []);
        setCategories(data.categories || []);
      })
      .catch((error) => {
        if (live) setState({ loading: false, error: error.message || 'Boutique indisponible.' });
      })
      .finally(() => { if (live) setState((current) => ({ ...current, loading: false })); });
    return () => { live = false; };
  }, [shopId]);

  const visible = useMemo(() => {
    const search = query.trim().toLowerCase();
    return products.filter((product) => {
      if (category && product.category !== category) return false;
      if (search && !`${product.name} ${product.description}`.toLowerCase().includes(search)) return false;
      return true;
    });
  }, [products, query, category]);

  // L'accent dérive du nom de la boutique (déterministe, sans stockage — voir
  // storeUtils.js): tous les visiteurs voient la même couleur, sans réglage.
  const accent = accentForShop(shop);

  if (state.loading) {
    return (
      <main className="stores-storefront" aria-busy="true" aria-label="Chargement de la boutique">
        <div className="stores-storefront-inner">
          <div className="stores-skeleton-row" style={{ height: 64, maxWidth: 420, margin: '0 auto 18px' }} />
          <div className="stores-storefront-grid" style={{ marginTop: 22 }}>
            {[0, 1, 2].map((key) => <div key={key} className="stores-skeleton-row" style={{ height: 240 }} />)}
          </div>
        </div>
      </main>
    );
  }

  if (!shop) {
    return (
      <main className="stores-storefront">
        <div className="stores-storefront-inner">
          <div className="stores-empty">
            <span className="stores-empty-icon" aria-hidden="true"><StoreIcon size={22} /></span>
            <h1>Boutique indisponible</h1>
            <p>{state.error || 'Cette vitrine est introuvable ou a été désactivée par son propriétaire.'}</p>
            <Link className="stores-btn stores-btn--primary" to="/">Découvrir NSARA</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="stores-storefront">
      <div className="stores-storefront-inner">
        <header className="stores-storefront-head">
          <span className="stores-storefront-mark" style={{ background: accent }} aria-hidden="true">
            {shop.logoUrl ? <img src={shop.logoUrl} alt="" /> : shopMonogram(shop.name)}
          </span>
          <h1 className="stores-storefront-name">{shop.name}</h1>
          {shop.description && <p className="stores-storefront-desc">{shop.description}</p>}
        </header>

        {products.length > 0 && (
          <div className="stores-storefront-toolbar">
            {categories.length >= 2 && (
              <div className="stores-chips" role="group" aria-label="Filtrer par catégorie">
                <button
                  type="button"
                  className={category === '' ? 'stores-chip is-active' : 'stores-chip'}
                  aria-pressed={category === ''}
                  onClick={() => setCategory('')}
                >
                  Tout
                </button>
                {categories.map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={category === item ? 'stores-chip is-active' : 'stores-chip'}
                    aria-pressed={category === item}
                    onClick={() => setCategory(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}
            <div className="stores-search">
              <Search size={16} aria-hidden="true" className="stores-search-icon" />
              <input
                className="stores-input"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Rechercher un produit"
                aria-label="Rechercher un produit"
              />
            </div>
          </div>
        )}

        {products.length === 0 ? (
          <div className="stores-empty">
            <span className="stores-empty-icon" aria-hidden="true"><StoreIcon size={22} /></span>
            <h2>Catalogue en préparation</h2>
            <p>{shop.name} n’a pas encore de produit en ligne. Revenez très bientôt.</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="stores-empty">
            <h2>Aucun produit ne correspond</h2>
            <p>Essayez un autre mot-clé ou une autre catégorie.</p>
            <button type="button" className="stores-btn stores-btn--outline" onClick={() => { setQuery(''); setCategory(''); }}>
              Réinitialiser
            </button>
          </div>
        ) : (
          <div className="stores-storefront-grid">
            {visible.map((product) => {
              const out = product.availability === 'OUT_OF_STOCK';
              return (
                <article className="stores-storefront-card" key={product.publicId}>
                  <span className="stores-storefront-thumb" aria-hidden="true">
                    {product.photoUrl ? <img src={product.photoUrl} alt="" loading="lazy" /> : <ImageOff size={26} />}
                  </span>
                  <div className="stores-storefront-body">
                    <h2 className="stores-storefront-product">{product.name}</h2>
                    <p className="stores-storefront-price">{formatMoney(product.sellingPrice)}</p>
                    {product.availability === 'LOW_STOCK' && <p className="stores-storefront-hint">Stock limité</p>}
                    {out
                      ? <button type="button" className="stores-btn stores-btn--outline stores-btn--block" disabled>Indisponible</button>
                      : (
                        <a
                          className="stores-btn stores-btn--primary stores-btn--block"
                          style={{ background: accent }}
                          href={buildPublicProductUrl(product.publicId)}
                        >
                          Commander
                        </a>
                      )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <footer className="stores-storefront-foot">
          Propulsé par <Link to="/">NSARA</Link>
        </footer>
      </div>
    </main>
  );
}
